// Header.js - Header oscuro estilo federación (barra superior + menú agrupado)
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../services/authService';
import { getAccessToken } from '../services/api';
import '../App.css';

// ── key unificada del carrito ────────────────────────────────────────────────
const CART_KEY = 'cart';

/** Lee el array crudo del carrito y devuelve la cantidad total de items. */
function getCartCount() {
  try {
    const raw = localStorage.getItem(CART_KEY);
    const items = raw ? JSON.parse(raw) : [];
    return items.reduce((sum, item) => sum + (item.cantidad || 0), 0);
  } catch {
    return 0;
  }
}
// ─────────────────────────────────────────────────────────────────────────────

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [cartCount, setCartCount] = useState(getCartCount);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Función para verificar y actualizar el estado de autenticación
  const checkAuthStatus = useCallback(() => {
    const loggedIn = localStorage.getItem('isLoggedIn');
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const adminStatus = localStorage.getItem('isAdmin') === 'true';
    const hasToken = !!getAccessToken();
    
    // Mostrar "Mi Perfil" solo cuando hay token (evita mostrar enlace que llevaría al login)
    if (loggedIn === 'true' && hasToken && (userData.username || userData.email || userData.nombre)) {
      setIsLoggedIn(true);
      setUser(userData);
      setIsAdmin(adminStatus);
    } else {
      setIsLoggedIn(false);
      setUser(null);
      setIsAdmin(false);
    }
  }, []);

  // ── carrito: actualizar contador ──────────────────────────────────────────
  const refreshCartCount = useCallback(() => setCartCount(getCartCount()), []);

  useEffect(() => {
    // Actualizar al montar
    refreshCartCount();
    checkAuthStatus();

    // Si el usuario está logueado, verificar si es admin (solo si no sabemos que NO es admin)
    const loggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const token = localStorage.getItem('access_token');
    const isAdminKnown = localStorage.getItem('isAdmin');
    
    // Solo verificar si no sabemos que NO es admin, o si no está definido
    if (loggedIn && token && isAdminKnown !== 'false') {
      import('../services/authService').then(({ checkIsAdmin }) => {
        checkIsAdmin().catch(err => {
          // Solo loguear errores que no sean 403 (acceso denegado es esperado para no-admins)
          const is403Error = err.message && (err.message.includes('403') || err.message.includes('Acceso solo para administradores'));
          if (!is403Error) {
            console.error('Header: Error al verificar admin:', err);
          }
        });
      });
    }
  }, [checkAuthStatus, refreshCartCount]);

  // ── escuchar eventos del carrito ────────────────────────────────────────
  useEffect(() => {
    // 'cart-updated' → mismo evento que disparan ProductDetailPage y CartPage
    window.addEventListener('cart-updated', refreshCartCount);
    // 'storage' → cambios desde otras pestañas
    window.addEventListener('storage', refreshCartCount);

    return () => {
      window.removeEventListener('cart-updated', refreshCartCount);
      window.removeEventListener('storage', refreshCartCount);
    };
  }, [refreshCartCount]);

  // Verificar autenticación cuando cambia la ruta
  useEffect(() => {
    checkAuthStatus();
  }, [location.pathname, checkAuthStatus]);

  // Verificación periódica temporal después del login (solo por 5 segundos)
  useEffect(() => {
    const interval = setInterval(() => {
      checkAuthStatus();
    }, 500); // Verificar cada 500ms
    
    // Limpiar después de 5 segundos
    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 5000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [checkAuthStatus, location.pathname]);

  // Escuchar eventos de login/logout y cambios de admin
  useEffect(() => {
    const handleAuthChange = () => {
      checkAuthStatus();
    };

    const checkAdminStatus = () => {
      const adminStatus = localStorage.getItem('isAdmin') === 'true';
      setIsAdmin(adminStatus);
      checkAuthStatus();
    };

    window.addEventListener('userLogin', handleAuthChange);
    window.addEventListener('userLogout', handleAuthChange);
    window.addEventListener('userLogin', checkAdminStatus);
    window.addEventListener('adminStatusChanged', checkAdminStatus);
    
    // Verificar isAdmin periódicamente después del login (solo por 10 segundos)
    const adminCheckInterval = setInterval(() => {
      checkAdminStatus();
    }, 500);
    
    const adminCheckTimeout = setTimeout(() => {
      clearInterval(adminCheckInterval);
    }, 10000);
    
    return () => {
      window.removeEventListener('userLogin', handleAuthChange);
      window.removeEventListener('userLogout', handleAuthChange);
      window.removeEventListener('userLogin', checkAdminStatus);
      window.removeEventListener('adminStatusChanged', checkAdminStatus);
      clearInterval(adminCheckInterval);
      clearTimeout(adminCheckTimeout);
    };
  }, [checkAuthStatus]);

  // Controlar scroll con menú abierto
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isMenuOpen]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isUserDropdownOpen && !e.target.closest('.user-dropdown')) {
        setIsUserDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isUserDropdownOpen]);


  // ── menús agrupados (Resultados / Miembros) ───────────────────────────────
  const [openGroup, setOpenGroup] = useState(null);

  useEffect(() => {
    setOpenGroup(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!openGroup) return undefined;
    const handleOutside = (e) => {
      if (!e.target.closest('.hdr-group')) setOpenGroup(null);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpenGroup(null);
    };
    document.addEventListener('click', handleOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('click', handleOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [openGroup]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsUserDropdownOpen(false);
    setOpenGroup(null);
  };
  const toggleUserDropdown = () => setIsUserDropdownOpen(prev => !prev);

  const handleAfiliacionClick = () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      navigate('/profile#afiliacion');
    } else {
      navigate('/login');
    }
    closeMenu();
  };

  const handleLogout = () => {
    logout();
    setIsLoggedIn(false);
    setUser(null);
    closeMenu();
    navigate('/');
    window.dispatchEvent(new Event('userLogout'));
  };

  const path = location.pathname;
  const isActive = (to) => (to === '/' ? path === '/' : path === to || path.startsWith(`${to}/`));

  const NAV = [
    {
      type: 'group',
      id: 'eventos',
      label: 'Eventos',
      items: [
        { to: '/events', label: 'Próximos eventos' },
        { to: '/profile/postular-evento', label: 'Postular un evento' },
      ],
    },
    {
      type: 'group',
      id: 'resultados',
      label: 'Resultados',
      items: [
        { to: '/rankings', label: 'Rankings' },
        { to: '/records', label: 'Récords' },
        { to: '/clasificacion', label: 'Clasificación y DOTS' },
      ],
    },
    {
      type: 'group',
      id: 'miembros',
      label: 'Miembros',
      items: [
        { to: '/members', label: 'Miembros' },
        { to: '/associates', label: 'Clubes asociados' },
      ],
    },
    { type: 'link', to: '/rules', label: 'Reglamentos' },
    { type: 'link', to: '/shop', label: 'Tienda' },
  ];

  const displayName =
    user?.username || (user?.nombre ? `${user.nombre} ${user.apellido || ''}`.trim() : user?.email || 'Usuario');

  const Chevron = () => (
    <svg className="hdr-chevron" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  return (
    <>
      {/* Barra superior */}
      <div className="hdr-topbar">
        <div className="hdr-topbar-inner">
          <a href="https://internationalpowerliftingleague.com/" target="_blank" rel="noopener noreferrer">IPL</a>
          <span aria-hidden="true">|</span>
          <a href="https://wrpf.pro/" target="_blank" rel="noopener noreferrer">WRPF</a>
          <span aria-hidden="true">|</span>
          {isLoggedIn ? (
            <Link to="/profile">Mi perfil</Link>
          ) : (
            <Link to="/login">Ingresar</Link>
          )}
        </div>
      </div>

      {/* Header principal (sticky) */}
      <header className="header-minimalista">
        <div className="header-logo">
          <Link to="/" onClick={closeMenu} className="logo-link" aria-label="ACHIPO - Inicio">
            <span className="logo-icon">
              <img src="/achipo.jpg" alt="" className="logo-img" />
            </span>
            <span className="logo-text">
              <span className="logo-brand">ACHIPO</span>
              <span className="logo-tagline">Powerlifting Chile</span>
            </span>
          </Link>
        </div>

        {/* Navegación Desktop */}
        <nav className="header-nav-desktop" aria-label="Principal">
          {NAV.map((item) =>
            item.type === 'link' ? (
              <Link key={item.to} to={item.to} className={`nav-item ${isActive(item.to) ? 'active' : ''}`}>
                {item.label}
              </Link>
            ) : (
              <div
                key={item.id}
                className={`hdr-group ${openGroup === item.id ? 'open' : ''}`}
                onMouseEnter={() => setOpenGroup(item.id)}
                onMouseLeave={() => setOpenGroup(null)}
              >
                <button
                  type="button"
                  className={`nav-item nav-item--button ${item.items.some((i) => isActive(i.to)) ? 'active' : ''}`}
                  aria-expanded={openGroup === item.id}
                  aria-haspopup="true"
                  onClick={() => setOpenGroup((prev) => (prev === item.id ? null : item.id))}
                >
                  {item.label}
                  <Chevron />
                </button>
                <div className="hdr-group-menu" role="menu">
                  {item.items.map((sub) => (
                    <Link
                      key={sub.to}
                      to={sub.to}
                      role="menuitem"
                      className={`hdr-group-item ${isActive(sub.to) ? 'active' : ''}`}
                      onClick={() => setOpenGroup(null)}
                    >
                      {sub.label}
                    </Link>
                  ))}
                </div>
              </div>
            )
          )}
        </nav>

        {/* Acciones derecha */}
        <div className="header-actions">
          <Link to="/cart" className="cart-icon" aria-label={`Carrito${cartCount > 0 ? ` (${cartCount})` : ''}`}>
            <svg className="cart-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9 22C9.55228 22 10 21.5523 10 21C10 20.4477 9.55228 20 9 20C8.44772 20 8 20.4477 8 21C8 21.5523 8.44772 22 9 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M20 22C20.5523 22 21 21.5523 21 21C21 20.4477 20.5523 20 20 20C19.4477 20 19 20.4477 19 21C19 21.5523 19.4477 22 20 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M1 1H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 6H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </Link>

          {isLoggedIn && (
            <div className="user-dropdown">
              <button
                className={`user-button ${isUserDropdownOpen ? 'active' : ''}`}
                onClick={toggleUserDropdown}
                aria-expanded={isUserDropdownOpen}
                aria-haspopup="true"
              >
                <span className="user-avatar" aria-hidden="true">
                  {isAdmin ? (
                    <svg viewBox="0 0 24 24" fill="none"><path d="M3 18h18l-2-10-5 4-2-6-2 6-5-4z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" /><path d="M4 21v-1a8 8 0 0 1 16 0v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                  )}
                </span>
                <span className="user-name">{displayName}</span>
                <Chevron />
              </button>
              <div className={`dropdown-menu ${isUserDropdownOpen ? 'open' : ''}`}>
                {(isAdmin || localStorage.getItem('isAdmin') === 'true') && (
                  <Link to="/admin" className="dropdown-item admin-item" onClick={() => setIsUserDropdownOpen(false)}>
                    <svg viewBox="0 0 24 24" fill="none" className="dropdown-icon">
                      <path d="M4 6H20M4 12H20M4 18H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M17 18V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Panel Admin
                  </Link>
                )}
                <Link to="/profile" className="dropdown-item" onClick={() => setIsUserDropdownOpen(false)}>
                  <svg viewBox="0 0 24 24" fill="none" className="dropdown-icon">
                    <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Mi Perfil
                </Link>
                <Link to="/profile/mis-eventos" className="dropdown-item" onClick={() => setIsUserDropdownOpen(false)}>
                  <svg viewBox="0 0 24 24" fill="none" className="dropdown-icon">
                    <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M16 3V7M8 3V7M3 11H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Mis eventos
                </Link>
                <button onClick={() => { setIsUserDropdownOpen(false); handleLogout(); }} className="dropdown-item logout-item">
                  <svg viewBox="0 0 24 24" fill="none" className="dropdown-icon">
                    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Cerrar Sesión
                </button>
              </div>
            </div>
          )}

          <button className="ach-pill ach-pill--grad nav-afiliacion-btn" onClick={handleAfiliacionClick}>
            Hazte miembro
          </button>

          {/* Botón Menú Móvil */}
          <button
            className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
            onClick={toggleMenu}
            aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={isMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>

        {/* Menú Móvil */}
        <div className={`mobile-menu ${isMenuOpen ? 'active' : ''}`}>
          <nav className="mobile-nav" aria-label="Menú móvil">
            <Link to="/" className="mobile-nav-item" onClick={closeMenu}>Inicio</Link>
            {NAV.map((item) =>
              item.type === 'link' ? (
                <Link key={item.to} to={item.to} className="mobile-nav-item" onClick={closeMenu}>
                  {item.label}
                </Link>
              ) : (
                <div key={item.id} className="mobile-nav-group">
                  <span className="mobile-nav-group-label">{item.label}</span>
                  {item.items.map((sub) => (
                    <Link key={sub.to} to={sub.to} className="mobile-nav-item mobile-nav-sub" onClick={closeMenu}>
                      {sub.label}
                    </Link>
                  ))}
                </div>
              )
            )}
            <button className="ach-pill ach-pill--grad mobile-afiliacion-btn" onClick={handleAfiliacionClick}>
              Hazte miembro
            </button>

            {isLoggedIn ? (
              <>
                <div className="mobile-user-info">
                  <span className="mobile-user-name">{user?.username || displayName}</span>
                </div>
                {isAdmin && (
                  <Link to="/admin" className="mobile-nav-item admin-item" onClick={closeMenu}>
                    Panel de Administración
                  </Link>
                )}
                <Link to="/profile" className="mobile-nav-item" onClick={closeMenu}>Mi Perfil</Link>
                <Link to="/profile/mis-eventos" className="mobile-nav-item" onClick={closeMenu}>Mis eventos</Link>
                <button onClick={handleLogout} className="mobile-logout-button">Cerrar Sesión</button>
              </>
            ) : (
              <Link to="/login" className="ach-pill ach-pill--light mobile-login-button" onClick={closeMenu}>
                Iniciar sesión
              </Link>
            )}
          </nav>
        </div>
      </header>

      {/* Overlay para menú móvil */}
      {isMenuOpen && <div className="menu-overlay" onClick={closeMenu}></div>}

      <style>{`
        .hdr-topbar {
          background: #000;
          color: #fff;
          font-family: var(--achipo-font);
          font-size: 0.8rem;
          font-weight: 600;
        }
        .hdr-topbar-inner {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 14px;
          padding: 7px clamp(16px, 4vw, 48px);
        }
        .hdr-topbar a { color: #fff; text-decoration: none; letter-spacing: 0.04em; }
        .hdr-topbar a:hover { color: var(--achipo-red-bright); }
        .hdr-topbar span { color: #555; }

        .header-minimalista {
          position: sticky;
          top: 0;
          z-index: 1000;
          background: #0b0b0b;
          padding: 0 clamp(16px, 4vw, 48px);
          min-height: var(--header-height, 74px);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1.5rem;
          border-bottom: 1px solid #1f1f1f;
          font-family: var(--achipo-font);
        }

        .header-logo { flex-shrink: 0; }
        .logo-link { display: flex; align-items: center; gap: 0.75rem; text-decoration: none; color: #fff; }
        .logo-icon {
          width: 46px; height: 46px; border-radius: 50%; overflow: hidden;
          display: flex; align-items: center; justify-content: center; background: #fff;
        }
        .logo-img { width: 100%; height: 100%; object-fit: cover; }
        .logo-text { display: flex; flex-direction: column; line-height: 1; }
        .logo-brand {
          font-family: var(--achipo-display);
          font-weight: 800;
          font-size: 1.35rem;
          letter-spacing: 0.04em;
          color: #fff;
        }
        .logo-tagline {
          margin-top: 4px;
          font-size: 0.56rem;
          letter-spacing: 0.42em;
          text-transform: uppercase;
          color: #bdbdbd;
        }

        .header-nav-desktop { display: flex; align-items: center; gap: 1.75rem; }

        .nav-item {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          min-height: 44px;
          color: #fff;
          text-decoration: none;
          font-size: 0.85rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          white-space: nowrap;
          transition: color 0.2s;
        }
        .nav-item--button { background: none; border: 0; padding: 0; cursor: pointer; font-family: inherit; }
        .nav-item:hover { color: var(--achipo-red-bright); }
        .nav-item.active { color: #fff; }
        .nav-item.active::after {
          content: '';
          position: absolute;
          left: 0; right: 0; bottom: 4px;
          height: 2px;
          background: var(--achipo-red-bright);
          border-radius: 1px;
        }
        .hdr-chevron { width: 12px; height: 12px; transition: transform 0.2s; }

        .hdr-group { position: relative; }
        .hdr-group.open .hdr-chevron { transform: rotate(180deg); }
        .hdr-group-menu {
          position: absolute;
          top: 100%;
          left: -16px;
          min-width: 220px;
          padding: 8px;
          background: #161616;
          border: 1px solid #2a2a2a;
          border-radius: 12px;
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45);
          opacity: 0;
          visibility: hidden;
          transform: translateY(-6px);
          transition: opacity 0.18s, transform 0.18s, visibility 0.18s;
        }
        .hdr-group.open .hdr-group-menu { opacity: 1; visibility: visible; transform: translateY(0); }
        .hdr-group-item {
          display: block;
          padding: 11px 14px;
          border-radius: 8px;
          color: #e5e5e5;
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 600;
        }
        .hdr-group-item:hover { background: #222; color: #fff; }
        .hdr-group-item.active { color: var(--achipo-red-bright); }

        .header-actions { display: flex; align-items: center; gap: 0.9rem; }

        .cart-icon {
          position: relative;
          display: flex; align-items: center; justify-content: center;
          width: 44px; height: 44px; border-radius: 50%;
          color: #fff; text-decoration: none;
          transition: background 0.2s;
        }
        .cart-icon:hover { background: #1f1f1f; color: #fff; }
        .cart-svg { width: 21px; height: 21px; }
        .cart-badge {
          position: absolute; top: 2px; right: 0;
          background: var(--achipo-red-bright); color: #fff;
          font-size: 0.68rem; font-weight: 700;
          min-width: 18px; height: 18px; border-radius: 9px;
          display: flex; align-items: center; justify-content: center; padding: 0 4px;
        }

        .user-dropdown { position: relative; }
        .user-button {
          display: flex; align-items: center; gap: 0.5rem;
          min-height: 44px; padding: 0 14px;
          background: #1b1b1b; border: 1px solid #2f2f2f; border-radius: 999px;
          font-size: 0.85rem; color: #fff; cursor: pointer; font-family: inherit;
          transition: border-color 0.2s;
        }
        .user-button:hover, .user-button.active { border-color: #555; }
        .user-avatar { display: inline-flex; width: 18px; height: 18px; font-size: 1rem; }
        .user-avatar svg { width: 18px; height: 18px; }
        .user-name { color: #fff; font-size: 0.85rem; font-weight: 600; max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .user-button.active .hdr-chevron { transform: rotate(180deg); }

        .dropdown-menu {
          position: absolute; top: calc(100% + 8px); right: 0;
          background: #161616; border: 1px solid #2a2a2a; border-radius: 12px;
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45);
          min-width: 210px; padding: 6px;
          opacity: 0; visibility: hidden; transform: translateY(-6px);
          transition: opacity 0.2s, transform 0.2s, visibility 0.2s;
          z-index: 1002;
        }
        .dropdown-menu.open { opacity: 1; visibility: visible; transform: translateY(0); }
        .dropdown-item {
          display: flex; align-items: center; gap: 0.75rem;
          width: 100%; padding: 0.7rem 0.9rem; border-radius: 8px;
          color: #e5e5e5; text-decoration: none; font-size: 0.88rem;
          background: none; border: none; cursor: pointer; font-family: inherit; text-align: left;
        }
        .dropdown-item:hover { background: #222; color: #fff; }
        .dropdown-icon { width: 16px; height: 16px; }
        .admin-item { color: #93b4ff; font-weight: 600; }
        .logout-item { color: #ff8a80; }

        .menu-toggle {
          display: none;
          flex-direction: column; justify-content: space-between;
          width: 44px; height: 44px; padding: 13px 10px;
          background: none; border: none; cursor: pointer; box-sizing: border-box;
        }
        .menu-toggle span { display: block; width: 100%; height: 2px; background: #fff; border-radius: 1px; transition: all 0.3s; }
        .menu-toggle.active span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
        .menu-toggle.active span:nth-child(2) { opacity: 0; }
        .menu-toggle.active span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

        .mobile-menu {
          position: fixed; top: 0; right: 0; bottom: 0; width: min(300px, 86vw);
          background: #0f0f0f; z-index: 1001;
          transform: translateX(100%); visibility: hidden;
          transition: transform 0.3s ease, visibility 0.3s;
          overflow-y: auto; box-shadow: -4px 0 24px rgba(0, 0, 0, 0.5);
        }
        .mobile-menu.active { transform: translateX(0); visibility: visible; }
        .mobile-nav { padding: 5.5rem 1.25rem 2rem; display: flex; flex-direction: column; gap: 0.25rem; }
        .mobile-nav-item {
          padding: 0.85rem 1rem; color: #fff; text-decoration: none; border-radius: 8px;
          font-size: 0.95rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
        }
        .mobile-nav-item:hover { background: #1b1b1b; color: #fff; }
        .mobile-nav-group { display: flex; flex-direction: column; margin: 0.35rem 0; }
        .mobile-nav-group-label {
          padding: 0.6rem 1rem 0.25rem; font-size: 0.72rem; font-weight: 700;
          letter-spacing: 0.18em; text-transform: uppercase; color: #8a8a8a;
        }
        .mobile-nav-sub { text-transform: none; letter-spacing: 0; font-weight: 600; padding-left: 1.5rem; }
        .mobile-afiliacion-btn, .mobile-login-button { margin-top: 1rem; width: 100%; }
        .mobile-user-info { margin: 1.25rem 0 0.25rem; padding: 0.85rem 1rem; background: #1b1b1b; border-radius: 8px; }
        .mobile-user-name { font-weight: 600; color: #fff; }
        .mobile-logout-button {
          margin-top: 0.75rem; min-height: 44px; background: none; border: 1px solid #333;
          color: #ff8a80; border-radius: 999px; font-size: 0.9rem; cursor: pointer; font-family: inherit;
        }
        .menu-overlay { display: block; position: fixed; inset: 0; background: rgba(0, 0, 0, 0.55); z-index: 999; }

        /* El nombre del usuario solo cabe junto al menú completo en pantallas anchas */
        @media (max-width: 1400px) {
          .user-name { display: none; }
        }
        @media (max-width: 1300px) {
          .header-nav-desktop { gap: 1.25rem; }
        }
        @media (max-width: 1200px) {
          .header-nav-desktop { display: none; }
          .menu-toggle { display: flex; }
          .nav-afiliacion-btn { display: none; }
        }
        @media (max-width: 480px) {
          .logo-tagline { display: none; }
          .logo-brand { font-size: 1.1rem; }
          .logo-icon { width: 38px; height: 38px; }
          .header-actions { gap: 0.25rem; }
          .user-dropdown { display: none; }
        }
      `}</style>
    </>
  );
};

export default Header;
